import {
  Box,
  Button,
  FormLabel,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  VStack,
  useToast,
} from '@chakra-ui/react';

import React, { useCallback, useState } from 'react';
import { useMutation } from 'urql';
import { useFlag } from '@unleash/proxy-client-react';
import {
  Project,
  TranslationType,
  UpdateProjectDocument,
} from '../../gql/graphql';

type SettingsProps = {
  project: Pick<Project, 'id' | 'settings'>;
};

export default function AdvancedSettingsTab({
  project,
}: SettingsProps): React.ReactElement {
  const [, updateProject] = useMutation(UpdateProjectDocument);

  const enablePolyCountLimitSetting = useFlag(
    'enable-poly-count-limit-setting'
  );

  const toast = useToast();

  const handleTranslationTypeSubmit = async (
    translationType: TranslationType
  ) => {
    await updateProject({
      input: {
        id: project.id,
        patch: {
          settings: {
            ...project.settings,
            translationType: translationType,
          },
        },
      },
    });
    toast({
      title: 'Translation type updated',
      status: 'success',
    });
  };

  const [polygonLimitValue, setPolygonLimitValue] = useState(
    project.settings?.polygonCalculationLimit || 5000
  );

  const handlePolygonLimitSubmit = useCallback(async () => {
    await updateProject({
      input: {
        id: project.id,
        patch: {
          settings: {
            ...project.settings,
            polygonCalculationLimit: polygonLimitValue,
          },
        },
      },
    });
    toast({
      title: 'Polygon calculation limit updated',
      status: 'success',
    });
    window.location.reload();
  }, [polygonLimitValue, project.id, project.settings, toast, updateProject]);

  return (
    <VStack alignItems={'start'}>
      <Box>
        <FormLabel>IFC translation type</FormLabel>
        <Select
          value={
            project.settings?.translationType
              ? project.settings?.translationType
              : 'modern'
          }
          // Patch
          onChange={(e) =>
            handleTranslationTypeSubmit(e.target.value as TranslationType)
          }
          variant={'filled'}
          borderRadius={'md'}
        >
          {Object.values(TranslationType).map((value) => (
            <option key={value}>{value}</option>
          ))}
        </Select>
      </Box>
      {enablePolyCountLimitSetting && (
        <Box>
          <FormLabel>Polygon calculation limit</FormLabel>
          <HStack>
            <NumberInput
              defaultValue={project.settings?.polygonCalculationLimit || 5000}
              min={500}
              max={50000}
              step={100}
              variant={'filled'}
              inputMode="numeric"
              precision={0}
              onChange={(_, numericValue) => setPolygonLimitValue(numericValue)}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            {polygonLimitValue !==
              project.settings?.polygonCalculationLimit && (
              <Button variant={'outline'} onClick={handlePolygonLimitSubmit}>
                Save and refresh
              </Button>
            )}
          </HStack>
        </Box>
      )}
    </VStack>
  );
}
